import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const OcrMappingContext = createContext(null);

// Fonction pour construire le mapping initial depuis l'answer
function buildInitialMapping(answer) {
  if (!answer?.blocks) return {};
  
  const map = {};
  
  const traverseBlocks = (blocks, parentPath = '') => {
    blocks.forEach(block => {
      if (!block.rows) return;
      
      block.rows.forEach((row, rowIndex) => {
        const rowPath = parentPath ? `${parentPath}.${block.id}.$items[${rowIndex}]` : `${block.id}.$items[${rowIndex}]`;
        
        // Stocker les fields avec geometry
        if (row.fields) {
          row.fields.forEach(field => {
            if (field.geometry) {
              const fieldPath = `${rowPath}.${field.id}`;
              map[fieldPath] = {
                geometry: field.geometry,
                value: field.value,
                id: field.id
              };
            }
          });
        }
        
        // Parcourir les blocs imbriqués
        if (row.blocks) {
          traverseBlocks(row.blocks, rowPath);
        }
      });
    });
  };
  
  traverseBlocks(answer.blocks);
  return map;
}

export function OcrMappingProvider({ children, initialAnswer }) {
  const [mapping, setMapping] = useState(() => {
    // Initialiser le mapping depuis l'answer au montage
    return buildInitialMapping(initialAnswer);
  });

  // Obtenir la geometry pour un field path
  const getGeometry = useCallback((fieldPath) => {
    return mapping[fieldPath]?.geometry || null;
  }, [mapping]);

  // Obtenir le field complet (avec geometry) pour un field path
  const getField = useCallback((fieldPath) => {
    return mapping[fieldPath] || null;
  }, [mapping]);

  // Mettre à jour le mapping pour un field spécifique
  const updateField = useCallback((fieldPath, data) => {
    setMapping(prev => ({
      ...prev,
      [fieldPath]: { ...prev[fieldPath], ...data }
    }));
  }, []);

  // Décaler les indices du mapping après une suppression
  const shiftIndicesAfterDelete = useCallback((blockPath, deletedIndex) => {
    setMapping(prev => {
      const newMapping = {};
      
      Object.keys(prev).forEach(key => {
        // Vérifier si cette clé fait partie du même bloc
        if (key.startsWith(blockPath)) {
          // Extraire l'index de cette clé
          const match = key.match(/\$items\[(\d+)\](?!.*\$items)/);
          if (match) {
            const currentIndex = parseInt(match[1], 10);
            
            // Si l'index est après celui supprimé, le décrémenter
            if (currentIndex > deletedIndex) {
              const newKey = key.replace(
                /\$items\[(\d+)\](?!.*\$items)/,
                `$items[${currentIndex - 1}]`
              );
              newMapping[newKey] = prev[key];
            } else if (currentIndex < deletedIndex) {
              // Garder tel quel si avant l'index supprimé
              newMapping[key] = prev[key];
            }
            // Ne pas copier si currentIndex === deletedIndex (on le supprime)
          } else {
            newMapping[key] = prev[key];
          }
        } else {
          // Garder les clés qui ne font pas partie de ce bloc
          newMapping[key] = prev[key];
        }
      });
      
      return newMapping;
    });
  }, []);

  // Ajouter un nouveau field au mapping
  const addField = useCallback((fieldPath, fieldData) => {
    setMapping(prev => ({
      ...prev,
      [fieldPath]: fieldData
    }));
  }, []);

  // Réinitialiser le mapping (utile pour changer de tâche)
  const resetMapping = useCallback((answer) => {
    setMapping(buildInitialMapping(answer));
  }, []);

  const value = useMemo(
    () => ({
      mapping,
      getGeometry,
      getField,
      updateField,
      shiftIndicesAfterDelete,
      addField,
      resetMapping,
    }),
    [mapping, getGeometry, getField, updateField, shiftIndicesAfterDelete, addField, resetMapping]
  );

  return (
    <OcrMappingContext.Provider value={value}>
      {children}
    </OcrMappingContext.Provider>
  );
}

OcrMappingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialAnswer: PropTypes.object,
};

export function useOcrMapping() {
  const context = useContext(OcrMappingContext);
  if (!context) {
    // Si pas de context, retourner des fonctions vides pour ne pas casser l'app
    return {
      mapping: {},
      getGeometry: () => null,
      getField: () => null,
      updateField: () => {},
      shiftIndicesAfterDelete: () => {},
      addField: () => {},
      resetMapping: () => {},
    };
  }
  return context;
}
